import React, { useEffect, useState } from 'react';
import Content from '../../layout/content/Content';
import Head from '../../layout/head/Head';
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  PaginationComponent,
  Row
} from '../../components/Component';
import { Badge, Card, UncontrolledDropdown, DropdownMenu, DropdownToggle, DropdownItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import AuthTenantLoader from '../loaders/AuthTenantLoader';
import AuthTenantPageError from '../error/AuthTenantPageError';
import AuthTenantNoData from '../error/AuthTenantNoData';
import {
  fail_retrieve_products,
  loading_retrieve_products,
  reset_retrieve_products,
  success_retrieve_products
} from '../../reducers/auth_tenant/retrieve_products_Slice';
import {
  RETIREVE_PRODUCT_ERROR_MSG,
  RETIREVE_PRODUCT_SUCCESS_MSG
} from '../../utils/error_success_messages';
import { RETRIEVE_SALES_REP_ALLOWED_PRODUCTS_LIST_API } from '../../api/api_data';
import { useSelector, useDispatch } from 'react-redux';
import * as app_consts from '../../utils/app_consts';
import { tenant_auth_actions_helper } from '../../actions/actions_helper';
import { addThreeDots, joinWithCommas, messageToast } from '../../utils/Utils';
import { useLocation, useNavigate } from 'react-router-dom';

const ProductsListings = ({ tenant_info, user_info }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sm, updateSm] = useState(false);
  const [itemPerPage] = useState(12);
  const [page_data, setPageData] = useState([]);
  const [current_page_data, setCurrentPageData] = useState([]);

  // Initialize state from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || "");
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || "all");
  const [activeStatus, setActiveStatus] = useState(searchParams.get('status') || "all");

  // Calculate pagination
  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = current_page_data?.slice(indexOfFirstItem, indexOfLastItem);

  // Get unique categories and statuses for filtering
  const categories = ["all", ...new Set(page_data?.map(item => item.category))].filter(Boolean);
  const statuses = ["all", "active", "inactive"];

  // Update URL when filters change
  const updateURL = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (activeCategory !== 'all') params.set('category', activeCategory);
    if (activeStatus !== 'all') params.set('status', activeStatus);
    params.set('page', currentPage);

    navigate(`?${params.toString()}`, { replace: true });
  };

  // Initial data fetch
  useEffect(() => {
    retrieve_products();
  }, []);

  // Update URL when filters change
  useEffect(() => {
    updateURL();
  }, [searchTerm, activeCategory, activeStatus, currentPage]);

  const retrieve_products = () => {
    setError("");
    dispatch(
      tenant_auth_actions_helper(
        loading_retrieve_products,
        success_retrieve_products,
        fail_retrieve_products,
        RETIREVE_PRODUCT_ERROR_MSG,
        RETIREVE_PRODUCT_SUCCESS_MSG,
        app_consts.GET,
        `/${tenant_info?.url_prefix}/${RETRIEVE_SALES_REP_ALLOWED_PRODUCTS_LIST_API}`
      )
    );
  };

  // Redux listener
  const retrieve_products_listener = useSelector(
    (state) => state.retrieve_products.retrieve_products
  );

  useEffect(() => {
    if (retrieve_products_listener.status !== app_consts.IDLE)
    {
      setLoading(retrieve_products_listener.status === app_consts.LOADING);
      setError("");

      if (retrieve_products_listener.status === app_consts.FAIL)
      {
        setError(retrieve_products_listener.message);
        messageToast(retrieve_products_listener.message, app_consts.FAIL);
      } else if (retrieve_products_listener.status === app_consts.SUCCESS)
      {
        setPageData(retrieve_products_listener.data);
        setCurrentPageData(retrieve_products_listener.data);
      }

      dispatch(reset_retrieve_products());
    }
  }, [retrieve_products_listener]);

  // Filter products based on search, category, and status
  useEffect(() => {
    filterProducts(searchTerm, activeCategory, activeStatus);
  }, [searchTerm, activeCategory, activeStatus, page_data]);

  const filterProducts = (searchItem, category, status) => {
    let filtered = [...page_data];

    // Apply category filter
    if (category && category !== "all")
    {
      filtered = filtered.filter(item => item.category === category);
    }

    // Apply status filter
    if (status && status !== "all")
    {
      filtered = filtered.filter(item =>
        status === "active" ? item.status === "active" : item.status !== "active"
      );
    }

    // Apply search filter
    if (searchItem)
    {
      filtered = filtered.filter((item) => {
        const combinedParams = [
          item.name,
          item.category,
          item.product_type,
          ...(item.colors || []),
          item.sku_no,
          ...(item.packet_sizes || []),
          item.status?.replace(/_/g, ' ').toUpperCase(),
          item.product_value ? `${parseFloat(item.product_value).toFixed(2)} ZMW` : ""
        ].join(" ").toLowerCase();

        return combinedParams.includes(searchItem.toLowerCase());
      });
    }

    setCurrentPageData(filtered);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Format product value
  const formatValue = (value) => {
    if (!value) return "--";
    return `${parseFloat(value).toFixed(2)} ZMW`;
  };

  // Product card component
  const ProductCard = ({ item }) => (
    <Col xxl={3} lg={4} md={6} sm={6} xs={6} key={item.id}>
      <Card
        className="product-card shadow-sm border-0 h-100"
        style={{
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          backgroundColor: '#ffffff'
        }}
      >
        <div className="product-thumb position-relative" style={{
          height: '140px',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#f5f5f5'
        }}>
          <img
            className="img-fluid"
            style={{
              objectFit: 'contain',
              maxHeight: '100%',
              maxWidth: '100%',
              padding: '0.75rem'
            }}
            src={item?.product_img || '/images/default-product.png'}
            alt={item?.name || "Product"}
            onError={(e) => {
              e.target.src = '/images/default-product.png';
            }}
          />
          <div className="product-badges position-absolute top-0 start-0 p-2 w-100 d-flex justify-content-between">
            <Badge color="secondary" className="px-2 py-1" style={{
              backgroundColor: '#555',
              color: '#fff',
              fontSize: '0.7rem'
            }}>
              {item?.sku_no ? `SKU: ${item?.sku_no}` : "SKU: --"}
            </Badge>
            <Badge color={item?.total_balance > 0 ? "success" : "danger"} className="px-2 py-1"
              style={{
                backgroundColor: item?.total_balance > 0 ? '#28a745' : '#dc3545',
                color: '#fff',
                fontSize: '0.7rem'
              }}>
              {item?.total_balance > 0 ? "In Stock" : "Out of Stock"}
            </Badge>
          </div>
        </div>

        <div className="card-inner p-3">
          {/* Top row with category and price */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Badge color="light" className="text-uppercase fw-bold" style={{
              backgroundColor: '#e9ecef',
              color: '#495057',
              fontSize: '0.7rem'
            }}>
              {item?.category || "Uncategorized"}
            </Badge>

            <div className="text-success fw-bold" style={{ fontSize: '0.9rem' }}>
              {formatValue(item?.product_value)}
            </div>
          </div>

          {/* Product title */}
          <h5 className="product-title fw-bold mb-2" style={{
            color: '#212529',
            fontSize: '0.95rem',
            minHeight: '30px'
          }}>
            {item?.name || "No Name"}
          </h5>

          {/* Product meta (sizes/colors) */}
          <div className="product-meta mb-3" style={{ minHeight: '40px' }}>
            {item?.packet_sizes?.length > 0 && (
              <div className="d-flex align-items-center mb-1">
                <Icon name="box" className="me-2 fs-6" style={{ color: '#6c757d' }}></Icon>
                <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                  Sizes: {addThreeDots(joinWithCommas(item.packet_sizes), 20)}
                </small>
              </div>
            )}
            {item?.colors?.length > 0 && (
              <div className="d-flex align-items-center mb-1">
                <Icon name="palette" className="me-2 fs-6" style={{ color: '#6c757d' }}></Icon>
                <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                  Colors: {addThreeDots(joinWithCommas(item.colors), 20)}
                </small>
              </div>
            )}
          </div>

          {/* Bottom row with availability and view button */}
          <div className="d-flex justify-content-between align-items-center mt-auto">
            <Badge color="light" style={{
              backgroundColor: '#e9ecef',
              color: '#212529',
              fontSize: '0.8rem'
            }}>
              <Icon name="layers" className="me-1"></Icon>
              {item?.total_balance || 0} Available
            </Badge>

            <Link
              to={{
                pathname: `/${tenant_info.url_prefix}/products/${item.id}`,
                search: location.search
              }}
              className="btn btn-sm btn-primary"
              style={{
                padding: '0.25rem 0.75rem',
                fontSize: '0.8rem'
              }}
            >
              View
            </Link>
          </div>
        </div>
      </Card>
    </Col>
  );

  return (
    <>
      <Head title={tenant_info?.name ? `${tenant_info.name} Products` : "Products"} />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page style={{ color: '#212529' }}>Product Catalogue</BlockTitle>
              <BlockDes className="text-muted">
                {!(loading || error) && (
                  <p>
                    Showing {currentItems?.length} of {current_page_data?.length} products
                    {activeCategory !== "all" && ` in ${activeCategory}`}
                  </p>
                )}
              </BlockDes>
            </BlockHeadContent>
            {!loading && (
              <Button
                color="primary"
                outline
                className="btn-dim"
                onClick={retrieve_products}
                style={{ borderColor: '#0d6efd', color: '#0d6efd' }}
              >
                <Icon name="refresh"></Icon>
                <span>Refresh</span>
              </Button>
            )}
          </BlockBetween>
        </BlockHead>

        {/* Filters Section */}
        {!loading && !error && page_data?.length > 0 && (
          <Card className="mb-4" style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}>
            <div className="card-inner">
              <Row className="g-3">
                <Col md="4">
                  <div className="form-control-wrap">
                    <div className="form-icon form-icon-right">
                      <Icon name="search" style={{ color: '#6c757d' }}></Icon>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ borderColor: '#ced4da' }}
                    />
                  </div>
                </Col>
                <Col md="2">
                  <UncontrolledDropdown>
                    <DropdownToggle color="light" outline className="bg-white dropdown-toggle w-100" style={{ borderColor: '#ced4da' }}>
                      {activeCategory === "all" ? "All Categories" : activeCategory}
                    </DropdownToggle>
                    <DropdownMenu className="w-100" style={{ borderColor: '#ced4da' }}>
                      <DropdownItem onClick={() => setActiveCategory("all")}>All Categories</DropdownItem>
                      {categories.filter(c => c !== "all").map(category => (
                        <DropdownItem
                          key={category}
                          onClick={() => setActiveCategory(category)}
                        >
                          {category}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </Col>
              </Row>
            </div>
          </Card>
        )}

        {loading ? (
          <AuthTenantLoader tenant_info={tenant_info} />
        ) : error ? (
          <AuthTenantPageError tenant_info={tenant_info} error_message={error} />
        ) : page_data?.length > 0 ? (
          <Block>
            {/* Products Grid */}
            <Row className="g-3">
              {currentItems?.length > 0 ? (
                currentItems.map(item => <ProductCard key={item.id} item={item} />)
              ) : (
                <Col className="text-center py-5">
                  <div className="nk-block">
                    <div className="nk-block-content">
                      <Icon name="package" size="2x" className="text-muted mb-3"></Icon>
                      <h5 style={{ color: '#212529' }}>No products found</h5>
                      <p className="text-muted">Try adjusting your search or filter criteria</p>
                      <Button
                        color="primary"
                        outline
                        onClick={() => {
                          setSearchTerm("");
                          setActiveCategory("all");
                          setActiveStatus("all");
                        }}
                        style={{ borderColor: '#0d6efd', color: '#0d6efd' }}
                      >
                        Clear All Filters
                      </Button>
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {/* Pagination */}
            {current_page_data?.length > itemPerPage && (
              <div className="mt-4 d-flex justify-content-center">
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={current_page_data?.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </div>
            )}
          </Block>
        ) : (
          <AuthTenantNoData tenant_info={tenant_info} no_data_message={"No Products Found"} />
        )}
      </Content>
    </>
  );
};

export default ProductsListings;